import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  runEfforts,
  runModelTiers,
  updateProjectSettingsSchema,
  type ProjectSettings,
  type UpdateProjectSettings,
} from "@zworks/console-contracts";
import { useState } from "react";
import { LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { archiveProject, projectSettingsQuery, updateProjectSettings, ApiError } from "@/lib/api";
import { SettingsFailure } from "./settings-feedback";
import { settingsAccessLost } from "@/lib/api";

export const settingsSelectClass =
  "h-9 rounded-md border border-input bg-card px-3 text-sm text-foreground outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground";
function editable(value: ProjectSettings): UpdateProjectSettings {
  return {
    name: value.name,
    description: value.description,
    timeZone: value.timeZone,
    defaultModelTier: value.defaultModelTier,
    defaultEffort: value.defaultEffort,
    perRunCap: value.perRunCap,
    concurrentRunLimit: value.concurrentRunLimit,
  };
}
export function GeneralSettingsSection({
  projectId,
  isOwner,
}: {
  projectId: string;
  isOwner: boolean;
}) {
  const queryClient = useQueryClient();
  const query = useQuery(projectSettingsQuery(projectId));
  const [draft, setDraft] = useState<UpdateProjectSettings | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  async function saved(value: ProjectSettings, message: string) {
    setDraft(null);
    setNotice(message);
    setConfirming(false);
    queryClient.setQueryData(projectSettingsQuery(projectId).queryKey, value);
    await queryClient.invalidateQueries({ queryKey: ["me", "projects"] });
    await queryClient.invalidateQueries({ queryKey: ["projects", projectId, "billing"] });
  }
  async function failed(error: unknown) {
    if (settingsAccessLost(error)) {
      setDraft(null);
      queryClient.removeQueries({ queryKey: projectSettingsQuery(projectId).queryKey });
      await queryClient.invalidateQueries({ queryKey: ["me", "projects"] });
    } else if (error instanceof ApiError && error.status === 409) {
      setDraft(null);
      await query.refetch();
    }
  }
  const save = useMutation({
    mutationFn: (input: UpdateProjectSettings) => updateProjectSettings(projectId, input),
    onSuccess: (value) => saved(value, "Project settings saved."),
    onError: failed,
  });
  const archive = useMutation({
    mutationFn: () => archiveProject(projectId),
    onSuccess: (value) => saved(value, "Project archived. New runs are blocked."),
    onError: failed,
  });
  const error = save.error ?? archive.error;
  if (settingsAccessLost(error)) return <SettingsFailure error={error} />;
  if (query.isPending) return <LoadingState label="Loading general settings" rows={4} />;
  if (query.isError)
    return <SettingsFailure error={query.error} retry={() => void query.refetch()} />;
  const form = draft ?? editable(query.data);
  const pending = save.isPending || archive.isPending;
  const disabled = !isOwner || pending;
  const changed = JSON.stringify(form) !== JSON.stringify(editable(query.data));
  function edit(patch: Partial<UpdateProjectSettings>) {
    setDraft({ ...form, ...patch });
    setNotice(null);
  }
  return (
    <section aria-label="General settings" className="flex max-w-3xl flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">General</h2>
        <Badge variant="outline">{query.data.status === "archived" ? "Archived" : "Active"}</Badge>
      </div>
      {!isOwner && (
        <p className="text-sm text-muted-foreground">
          Only a project owner can change these settings.
        </p>
      )}
      {notice && (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      )}
      {Boolean(error) && <SettingsFailure error={error} />}
      <form
        aria-label="General settings"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!disabled && changed) save.mutate(form);
        }}
      >
        <fieldset
          disabled={disabled}
          className="grid gap-4 rounded-lg border bg-card p-4 sm:grid-cols-2"
        >
          <legend className="px-1 text-sm font-medium">Project</legend>
          <label className="flex flex-col gap-1.5 text-sm">
            Project name
            <Input
              value={form.name}
              maxLength={120}
              required
              onChange={(event) => edit({ name: event.target.value })}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            Project address
            <Input aria-label="Project address" value={query.data.slug} readOnly />
            <span className="text-xs text-muted-foreground">Cannot be changed after creation.</span>
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            Description
            <Input
              value={form.description ?? ""}
              maxLength={500}
              onChange={(event) => edit({ description: event.target.value || null })}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            Time zone
            <Input
              aria-label="Time zone"
              value={form.timeZone}
              list="project-time-zones"
              required
              onChange={(event) => edit({ timeZone: event.target.value })}
            />
            <span className="text-xs text-muted-foreground">For schedules and reports.</span>
          </label>
          <datalist id="project-time-zones">
            {["UTC", ...Intl.supportedValuesOf("timeZone")].map((zone) => (
              <option key={zone} value={zone} />
            ))}
          </datalist>
        </fieldset>
        <fieldset
          disabled={disabled}
          className="grid gap-4 rounded-lg border bg-card p-4 sm:grid-cols-2"
        >
          <legend className="px-1 text-sm font-medium">Run defaults</legend>
          <label className="flex flex-col gap-1.5 text-sm">
            Default model tier
            <select
              className={settingsSelectClass}
              value={form.defaultModelTier}
              onChange={(event) =>
                edit({
                  defaultModelTier: event.target.value as UpdateProjectSettings["defaultModelTier"],
                })
              }
            >
              {runModelTiers.map((tier) => (
                <option key={tier} value={tier}>
                  {tier === "standard" ? "Standard" : "Deep reasoning"}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            Default effort
            <select
              className={settingsSelectClass}
              value={form.defaultEffort}
              onChange={(event) =>
                edit({
                  defaultEffort: event.target.value as UpdateProjectSettings["defaultEffort"],
                })
              }
            >
              {runEfforts.map((effort) => (
                <option key={effort} value={effort}>
                  {effort[0]!.toUpperCase() + effort.slice(1)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            Credit cap per run
            <Input
              aria-label="Credit cap per run"
              type="number"
              min={1}
              max={2147483647}
              required
              value={Number.isNaN(form.perRunCap) ? "" : form.perRunCap}
              onChange={(event) => edit({ perRunCap: event.target.valueAsNumber })}
            />
            <span className="text-xs text-muted-foreground">
              Held when a run starts. Unused credits are released.
            </span>
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            Concurrent runs
            <Input
              type="number"
              min={1}
              max={2147483647}
              required
              value={Number.isNaN(form.concurrentRunLimit) ? "" : form.concurrentRunLimit}
              onChange={(event) => edit({ concurrentRunLimit: event.target.valueAsNumber })}
            />
          </label>
        </fieldset>
        <p className="text-sm text-muted-foreground">
          Daily budget, quiet hours, and failure limits for automations are on the{" "}
          <a href="/automation#limits" className="text-accent-text hover:underline">
            Automation page
          </a>
          .
        </p>
        {isOwner && (
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={pending || !changed || !updateProjectSettingsSchema.safeParse(form).success}
            >
              {save.isPending ? "Saving…" : "Save settings"}
            </Button>
          </div>
        )}
      </form>
      {isOwner && (
        <section
          aria-label="Archive project"
          className="flex flex-wrap items-center gap-3 rounded-lg border p-4"
        >
          <div className="flex-1">
            <h3 className="text-sm font-medium">Archive project</h3>
            <p className="text-sm text-muted-foreground">
              Keep existing runs and audit history. An archived project accepts no new runs and can
              be restored within 30 days. Unused starter credits are forfeited.
            </p>
          </div>
          <Button
            variant="destructive"
            disabled={pending || query.data.status === "archived"}
            onClick={() => setConfirming(true)}
          >
            Archive project
          </Button>
        </section>
      )}
      <Dialog
        open={confirming}
        onOpenChange={(open) => {
          if (!pending) setConfirming(open);
        }}
      >
        <DialogContent>
          <DialogTitle>Archive {query.data.name}?</DialogTitle>
          <DialogDescription>
            New runs will be blocked. Existing runs and audit history are preserved.
          </DialogDescription>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" disabled={pending} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={pending} onClick={() => archive.mutate()}>
              {archive.isPending ? "Archiving…" : "Archive project"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
